import React from "react";

/* -------------------- SAFE JSON PARSER (RECURSIVE) -------------------- */
const tryParseJSON = (value) => {
    if (typeof value !== "string") return value;

    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
};

/* -------------------- NORMALIZER (handles nested JSON strings) -------------------- */
const normalizeData = (input) => {
    if (typeof input === "string") {
        const parsed = tryParseJSON(input);
        if (parsed !== input) return normalizeData(parsed);
        return input;
    }

    if (Array.isArray(input)) {
        return input.map(normalizeData);
    }

    if (typeof input === "object" && input !== null) {
        const result = {};
        Object.entries(input).forEach(([k, v]) => {
            result[k] = normalizeData(v);
        });
        return result;
    }

    return input;
};

/* -------------------- VALUE RENDERER -------------------- */
const renderValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return "-";
    }

    const parsedValue = normalizeData(value);

    if (Array.isArray(parsedValue)) {
        return parsedValue.map((item, i) => (
            <div key={i} className="ml-2 border-l pl-2 space-y-1">
                {typeof item === "object" ? (
                    <EmailFields selectedEmail={{ extractedFields: { data: item } }} />
                ) : (
                    <span>{String(item)}</span>
                )}
            </div>
        ));
    }

    if (typeof parsedValue === "object") {
        return (
            <EmailFields selectedEmail={{ extractedFields: { data: parsedValue } }} />
        );
    }

    return String(parsedValue);
};

/* -------------------- MAIN COMPONENT -------------------- */
export default function EmailFields({ selectedEmail }) {
    const rawData =
        selectedEmail?.extractedFields?.data ||
        selectedEmail?.extractedFields ||
        {};

    const data = normalizeData(rawData);

    return (
        <div className="max-h-[60vh] overflow-auto rounded-lg border border-slate-200 dark:border-white/10 shadow-sm bg-white dark:bg-slate-900">
            <table className="w-full text-sm text-left">
                <tbody>
                    {Object.entries(data).map(([key, value]) => {
                        if (value === null || value === undefined || value === "") return null;

                        return (
                            <tr
                                key={key}
                                className="group border-b border-slate-100 dark:border-white/5 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition"
                            >
                                {/* KEY COLUMN */}
                                <td className="w-1/3 px-4 py-3 bg-slate-50/70 dark:bg-slate-800/40 align-top">
                                    <div className="flex items-start gap-2">
                                        <div className="w-1 h-full bg-blue-500 rounded opacity-70 group-hover:opacity-100" />
                                        <span className="text-xs font-semibold text-slate-600 dark:text-slate-300 tracking-wide">
                                            {key
                                                .replace(/([A-Z])/g, " $1")
                                                .replace(/^./, (str) => str.toUpperCase())
                                                .trim()}
                                        </span>
                                    </div>
                                </td>

                                {/* VALUE COLUMN */}
                                <td className="w-2/3 px-4 py-3 text-slate-700 dark:text-white align-top">
                                    <div className="leading-relaxed break-words">
                                        {renderValue(value)}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}